import { Fragment } from 'react';
import _ from 'lodash';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import style from './card-select.module.css';
import MultiSelect from './multiselect';

export type CardSelectOptionValue = string | number | boolean;

export type CardSelectProps<Option extends CardSelectOptionValue> = {
  /** Maximum number of options user can select if multiSelect is true */
  maxSelectedOptions?: number;
  /** Enable multi-selection. */
  multiSelect?: boolean;
  /** Callback to be called when the selected value(s) change. */
  onChange?: (values: Option | Option[]) => void;
  /** The currently selected value(s). */
  value?: Option[] | undefined;
  /**
   * Automatically give focus to the selected card, or the first card if no card
   * is selected. This prop should be used sparingly, as it poses significant
   * accessibility concerns for individuals using screen readers. See
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#htmlattrdefautofocus
   * for more information.
   */
  autoFocus?: boolean;
  /** Flag to indicate if the options should not be selectable. */
  disabled?: boolean;
  /** Name attribute for <input> elements. */
  name: string;
  /**
   * A list of options for each individual Card.
   */
  options: {
    disabled?: boolean;
    label: string;
    value: Option;
    onClick?: () => void;
  }[];
};

export interface CardSelectItemProps {
  autoFocus?: boolean;
  disabled?: boolean;
  index: number;
  label: string;
  maxSelectedOptions?: number;
  name: string;
  onClick?: () => void;
  onChange: () => void;
  selectedValue?: CardSelectOptionValue | CardSelectOptionValue[];
  value: CardSelectOptionValue;
}

export const CardSelectItem: React.FC<CardSelectItemProps> = ({
  autoFocus,
  disabled,
  index,
  label,
  maxSelectedOptions,
  name,
  onClick = () => {},
  onChange,
  selectedValue,
  value,
}) => {
  const theme = useTheme();
  const isChecked = value === selectedValue;

  // only pass this prop in if props.autoFocus is true
  const autoFocusProp =
    // use autoFocus on the first selected card, and if none are selected, the first card
    autoFocus && (selectedValue ? isChecked : index === 0);

  const selectedOptionsCount = Array.isArray(selectedValue)
    ? selectedValue.length
    : 0;

  const isDisabled =
    disabled ||
    (!isChecked &&
      selectedOptionsCount === maxSelectedOptions &&
      maxSelectedOptions > 0);

  // disabling this rule because we do use nesting by putting the input inside of the label
  // this is valid according to this page https://github.com/evcohen/eslint-plugin-jsx-a11y/blob/master/docs/rules/label-has-for.md
  return (
    <Box
      sx={{
        textAlign: 'center',
        position: 'relative',
      }}
      key={value.toString()}
    >
      <Box
        component="label"
        sx={{
          padding: 3,
          userSelect: 'none',
          cursor: 'pointer',
          display: 'flex',
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'stretch',
          transition: 'background-color 150ms linear, border 150ms linear',
          border: '1px solid',
          backgroundColor: 'common.white',
          borderColor: 'neutral.lighter',
          borderRadius: '2px',
          boxShadow: 3,
          '&:hover': {
            [theme.breakpoints.up('sm')]: {
              backgroundColor: 'blue.lightest',
              borderColor: 'blue.main',
            },
          },
          ...(isChecked && {
            backgroundColor: 'blue.lightest',
            borderColor: 'blue.main',
          }),
          ...(isDisabled && {
            cursor: 'not-allowed',
            opacity: 0.5,
            '&:hover': {
              [theme.breakpoints.up('sm')]: {
                backgroundColor: 'common.white',
                borderColor: 'neutral.dark',
              },
            },
          }),
        }}
      >
        <Typography sx={{ alignSelf: 'center', whiteSpace: 'pre-line' }}>
          {label}
        </Typography>
        <input
          autoFocus={autoFocusProp}
          onClick={onClick}
          type={'radio'}
          className={style.screenReaderOnly}
          onChange={onChange}
          checked={isChecked}
          disabled={isDisabled}
          name={name}
        />
      </Box>
    </Box>
  );
};

/**
 * `CardSelect` components present a list of options for a user to choose from. The option selected
 * influences or changes the experience. Users can select a single option from the list.
 *
 * This iteration deprecates the multiple prop until usability issues are resolved. Please reach out
 * for more details.
 */
export const CardSelect: React.FC<CardSelectProps<CardSelectOptionValue>> = ({
  autoFocus,
  disabled,
  name,
  maxSelectedOptions,
  multiSelect,
  onChange,
  options,
  value: selectedValue,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const createChangeHandler = (value: CardSelectOptionValue) => () => {
    onChange?.(value);
  };

  return multiSelect ? (
    <MultiSelect
      autoFocus={autoFocus && !isMobile}
      disabled={disabled}
      name={name}
      maxSelectedOptions={maxSelectedOptions}
      onChange={onChange}
      options={options}
      value={selectedValue}
    />
  ) : (
    <Fragment>
      <Box
        sx={{
          display: 'grid',
          gridGap: '1rem',
          [theme.breakpoints.up('md')]: {
            gridGap: '1.5rem',
          },
          gridTemplateColumns:
            'repeat(auto-fill, minmax(min(250px, 100%), 1fr))',
          gridAutoRows: '1fr',
        }}
      >
        {options.map(
          ({ label, value, disabled: optionDisabled, onClick }, index) => {
            const onChange = createChangeHandler(value);
            return (
              <CardSelectItem
                key={value.toString()}
                autoFocus={autoFocus && !isMobile}
                disabled={disabled || optionDisabled}
                index={index}
                label={label}
                maxSelectedOptions={maxSelectedOptions}
                name={name}
                onClick={onClick}
                onChange={onChange}
                selectedValue={selectedValue}
                value={value}
              />
            );
          }
        )}
      </Box>
    </Fragment>
  );
};

export default CardSelect;
